import React, { useState } from "react";
import { Card } from "@my-scoot/component-library-legacy";
import useDesktopMediaQuery from "hooks/useDesktopMediaQuery";
import classnames from "classnames";
import ExlyPopover from "common/Components/ExlyPopover";
import ExlyImage from "common/Components/ExlyImage";
import { useHistory } from "react-router-dom";
import { dataProvider } from "data";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { useRefresh } from "react-admin";
import { default_template_preview_image } from "ui/pages/SalesPages/constants";
import LinkIcon from "@material-ui/icons/Link";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import {
  GJS_THANKYOU_BUILDER_ROUTE_GENERATORS,
  GJS_THANKYOU_BUILDER_ROUTE_KEYS,
} from "features/ThankYouPages/constants/ThankYouBuilder.constants";
import { checkPgBuilderSourceIsGJS } from "features/SalesPages/utils/checkPgBuilderSourceIsGJS";
import {
  thank_you_page_detail_keys,
  THANK_YOU_PAGES_ROUTE_KEYS,
  THANK_YOU_PAGES_ROUTE_GENERATORS,
} from "features/ThankYouPages/constants/ThankYouPages.constants";
import { thank_you_pages_apis } from "features/ThankYouPages/constants/ThankYouPages.api";
import { app_route_keys, app_pathnames } from "constants/urlPaths";
import { THANKYOU_PAGE_TYPES } from "ui/pages/ThankYou/constants/Thankyou.constants";
import { THANK_YOU_PAGE_TYPE_VALUES } from "features/ThankYouPages/constants/ThankYouPages.constants";
import { normalizeThankYouPageType } from "features/ThankYouPages/utils/normalizeThankYouPageType";
import { apiMethods } from "data/api.constants";
import { logError } from "utils/error";
import { ExlyModal } from "features/Common/modules/ExlyModal/ExlyModal";
import { ExlyInput } from "common/form";
import { is_empty } from "features/Common/utils/common.utils";
import debounce from "lodash/debounce";
import { MenuButtonComponent } from "features/SalesPages/modules/SalesPagesListItemCard/modules/MenuButtonComponent";
import { MenuPopoverComponent } from "features/SalesPages/modules/SalesPagesListItemCard/modules/MenuPopoverComponent/MenuPopoverComponent";
import {
  getCheckoutPageShareUrl,
  getPaymentPageShareUrl,
} from "features/CheckoutPages/CheckoutPages.utils";
import { useFetchListingDetails } from "features/Listings/utils/useFetchListingDetails";
import { LazyLoadedChangeThankYouOfferingModal } from "features/ThankYouPages/modules/ChangeThankYouOfferingModal/LazyLoadedChangeThankYouOfferingModal";
import {
  THANK_YOU_LIST_ITEM_TEXTS,
  THANK_YOU_LIST_ITEM_DIMENSIONS,
  THANK_YOU_LIST_ITEM_DEBOUNCE_MS,
} from "./ThankYouPagesListItemCard.constants";

export const ThankYouPagesListItemCard = ({ data }) => {
  const isDesktop = useDesktopMediaQuery();
  const history = useHistory();
  const refresh = useRefresh();
  const { notify } = useNotifications();

  const isPublished = data.is_published;
  const thankYouPageUuid = data[thank_you_page_detail_keys.uuid];

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditTitleModal, setShowEditTitleModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [titleValue, setTitleValue] = useState(data.title || "");
  const [duplicating, setDuplicating] = useState(false);
  const [showChangeOfferingModal, setShowChangeOfferingModal] = useState(false);
  const [thankYouListingDetails, setThankYouListingDetails] = useState({});
  const [copyingLink, setCopyingLink] = useState(false);
  const { fetchListingDetails } = useFetchListingDetails();

  const builderRoute = GJS_THANKYOU_BUILDER_ROUTE_GENERATORS[
    GJS_THANKYOU_BUILDER_ROUTE_KEYS.update
  ]({
    thankYouPageUuid,
    templateUuid: data.template_uid,
  });

  const onEditClick = () => {
    const pageBuilderSource =
      data.page_builder_source ?? data?.metadata?.page_builder_source;
    const isGjs = checkPgBuilderSourceIsGJS(pageBuilderSource);
    if (isGjs && !isDesktop) {
      notify(
        THANK_YOU_LIST_ITEM_TEXTS.DESKTOP_ONLY_EDIT,
        notification_color_keys.error
      );
      return;
    }
    if (isGjs) {
      history.push(builderRoute);
      return;
    }
    // Classic / non-GJS: route to legacy thank-you editor (listing or payment-link)
    const isOptIn =
      normalizeThankYouPageType(
        data?.page_type ?? data?.metadata?.page_type
      ) === THANK_YOU_PAGE_TYPE_VALUES.OPTIN;
    const pageTypeKey = isOptIn
      ? THANKYOU_PAGE_TYPES.OPTIN.key
      : THANKYOU_PAGE_TYPES.PURCHASE.key;
    const isComboLike =
      !data?.listing_uuid &&
      Boolean(
        data?.dynamic_link_id ||
          data?.payment_link_uuid ||
          data?.payment_link_identifier
      );
    if (isComboLike) {
      history.push(
        app_pathnames[app_route_keys.update_thankyou]({
          type: pageTypeKey,
          combo_uuid:
            data?.dynamic_link_id ||
            data?.payment_link_uuid ||
            data?.payment_link_identifier,
          isCombo: true,
        })
      );
      return;
    }
    history.push(
      app_pathnames[app_route_keys.update_thankyou]({
        type: pageTypeKey,
        listing_uuid: data?.listing_uuid,
        dynamic_link_uid: data?.dynamic_link_id,
        isCombo: false,
      })
    );
  };

  // Copy-link now gives the CHECKOUT page link of the linked offering
  // (not the thank-you cdn_url), mirroring ShareListingPopup's checkout share.
  // Falls back to the published thank-you URL when no offering is linked or
  // the listing fetch fails.
  const onCopyLinkClick = async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (copyingLink) return;
    const fallbackUrl = data?.metadata?.cdn_url;
    const copyText = async (text) => {
      if (!text || !navigator?.clipboard) return false;
      await navigator.clipboard.writeText(text);
      notify(
        THANK_YOU_LIST_ITEM_TEXTS.LINK_COPIED,
        notification_color_keys.info
      );
      return true;
    };
    // Payment-link backed thank-you page -> payment/checkout share URL.
    const paymentLinkId =
      data?.dynamic_link_id ||
      data?.payment_link_uuid ||
      data?.payment_link_identifier;
    if (!data?.listing_uuid && paymentLinkId) {
      try {
        const done = await copyText(
          getPaymentPageShareUrl({ dynamic_link_id: paymentLinkId })
        );
        if (done) return;
      } catch (err) {
        logError({
          error: err,
          occuredAt: "ThankYouPagesListItemCard.copyCheckoutLink",
        });
      }
      if (is_empty(fallbackUrl)) return;
      copyText(fallbackUrl);
      return;
    }
    if (is_empty(data?.listing_uuid)) {
      if (is_empty(fallbackUrl)) return;
      copyText(fallbackUrl);
      return;
    }
    setCopyingLink(true);
    try {
      const listingDetails = await fetchListingDetails({
        listingUuid: data.listing_uuid,
      });
      const checkoutUrl = listingDetails
        ? getCheckoutPageShareUrl(listingDetails)
        : "";
      if (!is_empty(checkoutUrl)) {
        await copyText(checkoutUrl);
        return;
      }
    } catch (err) {
      logError({
        error: err,
        occuredAt: "ThankYouPagesListItemCard.copyCheckoutLink",
      });
    } finally {
      setCopyingLink(false);
    }
    if (is_empty(fallbackUrl)) return;
    copyText(fallbackUrl);
  };

  const onChangeOfferingClick = async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (data?.listing_uuid) {
      try {
        const listingDetails = await fetchListingDetails({
          listingUuid: data.listing_uuid,
        });
        setThankYouListingDetails(listingDetails || {});
      } catch (err) {
        logError({
          error: err,
          occuredAt: "ThankYouPagesListItemCard.changeOffering",
        });
        setThankYouListingDetails({});
      }
    } else {
      setThankYouListingDetails({});
    }
    setShowChangeOfferingModal(true);
  };

  const onPreviewClick = (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (is_empty(thankYouPageUuid)) return;
    const previewRoute = THANK_YOU_PAGES_ROUTE_GENERATORS[
      THANK_YOU_PAGES_ROUTE_KEYS.thank_you_pages_preview
    ]({ thankYouPageUuid });
    history.push(`/${previewRoute}`);
  };

  const onDuplicateClick = debounce(() => {
    if (duplicating) return;
    setDuplicating(true);
    dataProvider
      .custom_request(
        thank_you_pages_apis.duplicate_thank_you_page,
        apiMethods.POST,
        {
          thankyou_page_uuid: thankYouPageUuid,
        }
      )
      .then(() => {
        notify(
          THANK_YOU_LIST_ITEM_TEXTS.DUPLICATE_SUCCESS,
          notification_color_keys.success
        );
        refresh();
      })
      .catch((err) => {
        logError({
          error: err,
          occuredAt: "ThankYouPagesListItemCard.duplicate",
        });
        notify(
          THANK_YOU_LIST_ITEM_TEXTS.ERROR_GENERIC,
          notification_color_keys.error
        );
      })
      .finally(() => setDuplicating(false));
  }, THANK_YOU_LIST_ITEM_DEBOUNCE_MS);

  const onDeleteClick = () => {
    setDeleting(true);
    dataProvider
      .custom_request(
        thank_you_pages_apis.delete_thank_you_page,
        apiMethods.POST,
        {
          thankyou_page_uuid: thankYouPageUuid,
        }
      )
      .then(() => {
        notify(
          THANK_YOU_LIST_ITEM_TEXTS.DELETE_SUCCESS,
          notification_color_keys.success
        );
        refresh();
      })
      .catch((err) => {
        logError({ error: err, occuredAt: "ThankYouPagesListItemCard.delete" });
        notify(
          THANK_YOU_LIST_ITEM_TEXTS.ERROR_GENERIC,
          notification_color_keys.error
        );
      })
      .finally(() => {
        setDeleting(false);
        setShowDeleteModal(false);
      });
  };

  const onEditTitleSave = () => {
    if (is_empty(titleValue)) return;
    dataProvider
      .custom_request(
        thank_you_pages_apis.update_thank_you_page(thankYouPageUuid),
        apiMethods.PUT,
        { updated_values: { title: titleValue } }
      )
      .then(() => {
        notify(
          THANK_YOU_LIST_ITEM_TEXTS.TITLE_UPDATED,
          notification_color_keys.success
        );
        refresh();
        setShowEditTitleModal(false);
      })
      .catch((err) => {
        logError({
          error: err,
          occuredAt: "ThankYouPagesListItemCard.editTitle",
        });
        notify(
          THANK_YOU_LIST_ITEM_TEXTS.ERROR_GENERIC,
          notification_color_keys.error
        );
      });
  };

  const moreOptionsJSX = (
    <div>
      <ExlyPopover
        AnchorComponent={MenuButtonComponent}
        PopoverComponent={(popoverComponentProps) => (
          <MenuPopoverComponent
            {...popoverComponentProps}
            onEditSalesPageClick={onEditClick}
            onEditSalePageTitleClick={() => {
              setTitleValue(data.title || "");
              setShowEditTitleModal(true);
            }}
            onChangeOfferingClick={onChangeOfferingClick}
            onDuplicatePageClick={onDuplicateClick}
            onDeletePageClick={() => setShowDeleteModal(true)}
          />
        )}
        popoverProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "top", horizontal: "right" },
          classes: {
            paper:
              "tw-shadow-[2px_2px_4px_rgba(0,0,0,0.12)] tw-rounded-[12px] tw-border tw-border-solid tw-border-[#e7e6e4] max-md:tw-rounded-t-[12px] max-md:tw-rounded-b-none",
          },
        }}
        onClose={(e) => {
          if (e?.stopPropagation) e.stopPropagation();
        }}
        forceDesktopDesign={false}
      />
    </div>
  );

  const statusChipJSX = (
    <div className="tw-flex tw-justify-center tw-items-center">
      <span
        className={classnames(
          "tw-rounded-full tw-text-white tw-text-[12px] tw-font-normal tw-py-1 tw-px-2 tw-leading-none tw-inline-flex tw-items-center tw-w-fit md:tw-justify-self-center",
          isPublished ? "tw-bg-[#00ac72]" : "tw-bg-[#ffaa00]"
        )}
      >
        {isPublished
          ? THANK_YOU_LIST_ITEM_TEXTS.PUBLISHED
          : THANK_YOU_LIST_ITEM_TEXTS.DRAFT}
      </span>
    </div>
  );

  const previewImage = default_template_preview_image;

  const copyLinkButtonJSX = isPublished ? (
    <LinkIcon
      className={classnames("tw-rotate-[-45deg] tw-cursor-pointer")}
      onClick={onCopyLinkClick}
    />
  ) : null;

  const editButtonJSX = <EditIcon className="tw-cursor-pointer" />;

  const previewButtonJSX = isPublished ? (
    <VisibilityOutlinedIcon
      className="tw-cursor-pointer"
      onClick={onPreviewClick}
    />
  ) : null;

  return (
    <>
      {isDesktop ? (
        <div
          className="tw-cursor-pointer tw-grid tw-grid-cols-[489fr_150fr_300fr] tw-gap-x-[10px] tw-bg-white tw-border-[1.5px] tw-border-solid tw-border-[#e7e6e4] tw-py-[24px] tw-px-[12px] tw-grid-flow-row first:tw-rounded-t-[4px] last:tw-rounded-b-[4px] last:tw-border-b-[3px]"
          onClick={onEditClick}
        >
          <div className={classnames("tw-flex tw-items-center", "ellipsis")}>
            <ExlyImage
              src={previewImage}
              height={THANK_YOU_LIST_ITEM_DIMENSIONS.PREVIEW_IMAGE_HEIGHT}
              width={THANK_YOU_LIST_ITEM_DIMENSIONS.PREVIEW_IMAGE_WIDTH}
              className="tw-object-cover tw-rounded-[5px] tw-mr-3 tw-min-w-[132px] tw-max-w-[132px]"
            />
            <div
              className={classnames(
                "tw-flex tw-flex-col tw-justify-between",
                "ellipsis"
              )}
            >
              <div
                className={classnames(
                  "tw-text-[#272522] tw-text-[16px] tw-font-medium",
                  "ellipsis"
                )}
                title={data.title}
              >
                {data.title}
              </div>
              <div
                className={classnames(
                  "tw-text-[12px] tw-font-normal tw-truncate tw-text-[rgba(0,0,0,0.4)]",
                  "ellipsis"
                )}
                title={data.listing_title}
              >
                {data.listing_title}
              </div>
            </div>
          </div>
          {statusChipJSX}
          <div className="tw-flex tw-items-center tw-justify-between tw-text-[#49494c]">
            <div className="tw-flex tw-items-center tw-gap-4">
              {copyLinkButtonJSX}
              {editButtonJSX}
              {previewButtonJSX}
            </div>
            {moreOptionsJSX}
          </div>
        </div>
      ) : (
        <Card
          shadow="none"
          className="tw-cursor-pointer tw-rounded-[5px] tw-border-[1.5px] tw-border-solid tw-border-[#e7e6e4] odd:tw-bg-white even:tw-bg-[#f7f7f7]"
          onClick={onEditClick}
        >
          <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
            <div
              className={classnames(
                "tw-text-[#272522] tw-text-[16px] tw-font-medium",
                "ellipsis"
              )}
            >
              {data.title}
            </div>
            {moreOptionsJSX}
          </div>
          <div className="m-1 p-2 d-flex">
            <div
              className={classnames(
                "tw-w-[90px] tw-min-w-[90px] tw-max-w-[90px] tw-h-[90px] tw-overflow-hidden",
                "rounded border mr-3"
              )}
            >
              <ExlyImage
                src={previewImage}
                className="tw-object-cover tw-w-full tw-h-full"
              />
            </div>
            <div className="d-flex flex-column w-100 ellipsis">
              <div className="text-muted small">Created for</div>
              <div
                className={classnames(
                  "tw-text-[12px] tw-font-normal tw-text-[rgba(0,0,0,0.85)]",
                  "ellipsis"
                )}
              >
                {data.listing_title}
              </div>
              {statusChipJSX}
            </div>
          </div>
        </Card>
      )}
      <ExlyModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        primaryBtnText={THANK_YOU_LIST_ITEM_TEXTS.DELETE_BTN}
        onPrimaryBtnClick={onDeleteClick}
        primaryBtnProps={{ loading: deleting }}
        title={THANK_YOU_LIST_ITEM_TEXTS.DELETE_TITLE}
        modal_props={{
          modalPaperClassName: "!tw-min-w-full md:!tw-min-w-[460px]",
        }}
      >
        <div className="tw-p-4">{THANK_YOU_LIST_ITEM_TEXTS.DELETE_BODY}</div>
      </ExlyModal>
      <ExlyModal
        open={showEditTitleModal}
        onClose={() => setShowEditTitleModal(false)}
        primaryBtnText={THANK_YOU_LIST_ITEM_TEXTS.SAVE_BTN}
        onPrimaryBtnClick={onEditTitleSave}
        title={THANK_YOU_LIST_ITEM_TEXTS.EDIT_TITLE}
        modal_props={{
          modalPaperClassName: "!tw-min-w-full md:!tw-min-w-[460px]",
        }}
      >
        <div className="tw-p-4">
          <ExlyInput
            fullWidth
            label={THANK_YOU_LIST_ITEM_TEXTS.EDIT_TITLE_LABEL}
            input={{
              value: titleValue,
              onChange: (e) => setTitleValue(e.target.value),
            }}
          />
        </div>
      </ExlyModal>
      {showChangeOfferingModal ? (
        <LazyLoadedChangeThankYouOfferingModal
          open={showChangeOfferingModal}
          onClose={() => setShowChangeOfferingModal(false)}
          thankYouPageData={data}
          thankYouListingDetails={thankYouListingDetails}
        />
      ) : null}
    </>
  );
};
